// Lead-capture panel. Unlike service bookings this is non-instant —
// the tenant's sales team follows up. Widget gathers the structured
// details and posts to the submit_enquiry RPC.

import type React from "react";
import { useState, useTransition } from "react";
import type { WidgetTenant, WidgetVenue } from "../types";
import {
  GUEST_BOOKING_CONSENTS,
  LEGAL_DOCUMENT_VERSIONS,
  documentSlugForGuestConsent,
} from "@vertu/shared";
import { supabase } from "../config";
// Enquiry replies go out by WhatsApp / SMS as well as email, and both need
// E.164 — a raw "0821234567" reaches the outbox and silently fails to route.
// Phone is optional here (email OR phone), hence the optional variant.
import { normalizeOptionalPhone } from "../lib/phone";
import { recordAttribution } from "../state/attribution";
import {
  consentWasInherited,
  setMarketingConsent,
  trackLead,
} from "../state/tracking";
import { pushHostLead } from "../state/host-datalayer";

// Server rejection reasons → guest-readable copy. The RPC returns HTTP 200
// with { ok:false, reason } — showing the raw code ("venue_required") read
// as a system failure to guests.
const REASON_COPY: Record<string, string> = {
  name_required: "Your name is required.",
  contact_required: "Add an email or phone number so we can reply.",
  event_date_required: "Please pick a preferred date.",
  venue_required: "Please choose a venue.",
  function_type_required: "Please choose a function type.",
  function_type_not_offered: "That venue doesn't host this type of function — pick another venue.",
  invalid_service: "That subject can't take enquiries right now — pick another one.",
  invalid_kind: "Pick what your enquiry is about.",
};

const KINDS: { value: string; label: string }[] = [
  { value: "wedding", label: "Wedding" },
  { value: "conference", label: "Conference / corporate" },
  { value: "private_event", label: "Private event" },
  { value: "large_group", label: "Large group booking" },
  { value: "other", label: "Other" },
];

export function EnquiryPanel({
  tenant,
  venues = [],
  enquiryServices = [],
  initialServiceId = null,
  chipCase,
  heading,
  tagline,
  kindLabels,
}: {
  tenant: WidgetTenant;
  /**
   * Tenant's bookable venues, sourced from `functions.venues` in
   * get_widget_config (filtered to `is_function_space = true`). When
   * empty the venue dropdown collapses to just "No preference" — the
   * tenant hasn't configured any function venues yet so there's
   * nothing to ask the customer to choose from.
   */
  venues?: readonly WidgetVenue[];
  /** Enquiry-enabled services offered as subjects (enquiry_enabled = true). */
  enquiryServices?: readonly { id: string; name: string }[];
  /** Pre-scope the form to a service (from a per-service Enquire CTA). */
  initialServiceId?: string | null;
  /**
   * Tenant CSS text-transform for the subject chips (settings.enquiry_chip_case)
   * — "uppercase" | "capitalize" | "none"/undefined — so the event-kind chips
   * and service chips read consistently (e.g. all-caps to match service names).
   */
  chipCase?: string;
  /** Tenant override for the panel heading (settings.enquiry_title). */
  heading?: string;
  /** Tenant override for the hero tagline (settings.enquiry_tagline). */
  tagline?: string;
  /**
   * Tenant label overrides for the event-kind chips, keyed by kind value
   * (settings.enquiry_kind_labels, e.g. { wedding: "Weddings & vow renewals" }).
   * Values stay fixed — the server whitelists them — only the display changes.
   */
  kindLabels?: Record<string, string>;
}) {
  const [pending, startTransition] = useTransition();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Subject: an event kind OR one of the tenant's enquiry-enabled services.
  const [serviceId, setServiceId] = useState<string | null>(
    initialServiceId &&
      enquiryServices.some((s) => s.id === initialServiceId)
      ? initialServiceId
      : null,
  );
  const [kind, setKind] = useState<string>("wedding");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [company, setCompany] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [flexible, setFlexible] = useState(true);
  const [partySize, setPartySize] = useState("");
  const [budget, setBudget] = useState("");
  const [venue, setVenue] = useState("");
  const [message, setMessage] = useState("");
  // POPIA marketing opt-in. Unticked by default (consent must be freely given),
  // EXCEPT when the guest already granted it on the tenant's own site and the
  // site tag vouched for it — see the note in CartPanel. Gates both the browser
  // Lead pixel (via setMarketingConsent) and the server-side lead conversion
  // (via the consent_log 'marketing' row).
  const [consentMarketing, setConsentMarketing] = useState(() =>
    consentWasInherited(),
  );

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (!name.trim()) return setError("Your name is required.");
    if (!email.trim() && !phone.trim()) {
      return setError("Add an email or phone number so we can reply.");
    }

    // Store the number the way we'll message it. Anything we can't turn into
    // E.164 is rejected here rather than saved — a lead whose phone can't be
    // dialled looks contactable in the inbox and never receives the reply.
    const phoneNorm = normalizeOptionalPhone(phone);
    if (!phoneNorm.ok) {
      setPhoneError(phoneNorm.error ?? null);
      return setError(phoneNorm.error ?? "Check the phone number.");
    }
    setPhoneError(null);
    if (phoneNorm.e164 && phoneNorm.e164 !== phone) setPhone(phoneNorm.e164);

    // Clamp to int4 range — submit_enquiry takes an integer, and an over-large
    // budget (typo'd cents, "100000000") overflowed it before the RPC even ran.
    const budgetRands = budget ? parseFloat(budget) : NaN;
    const budgetCents = Number.isFinite(budgetRands)
      ? Math.min(Math.round(budgetRands * 100), 2_000_000_000)
      : null;
    const partyCount = partySize ? parseInt(partySize, 10) : null;
    const referrer =
      typeof window !== "undefined" ? window.location.href : null;

    startTransition(async () => {
      const { data, error: rpcErr } = serviceId
        ? await supabase.rpc("submit_service_enquiry", {
            p_tenant_id: tenant.id,
            p_service_id: serviceId,
            p_contact_name: name.trim(),
            p_contact_email: email.trim() || null,
            p_contact_phone: phoneNorm.e164,
            p_company: company.trim() || null,
            p_event_date_preferred: eventDate || null,
            p_party_size: partyCount,
            p_budget_cents_approx: budgetCents,
            p_message: message.trim() || null,
            p_referrer_url: referrer,
          })
        : await supabase.rpc("submit_enquiry", {
            p_tenant_id: tenant.id,
            p_kind: kind,
            p_contact_name: name.trim(),
            p_contact_email: email.trim() || null,
            p_contact_phone: phoneNorm.e164,
            p_company: company.trim() || null,
            p_event_date_preferred: eventDate || null,
            p_event_date_flexible: flexible,
            p_party_size: partyCount,
            p_event_duration_hours: null,
            p_budget_cents_approx: budgetCents,
            p_venue_preference: venue.trim() || null,
            p_message: message.trim() || null,
            p_service_interest: null,
            p_referrer_url: referrer,
          });

      if (rpcErr) {
        setError(rpcErr.message);
        return;
      }
      const resp = data as
        | { ok?: boolean; reason?: string; enquiry_id?: string }
        | null;
      if (!resp?.ok) {
        const reason = resp?.reason ?? "";
        setError(
          REASON_COPY[reason] ??
            `Couldn't submit${reason ? ` (${reason})` : ""} — please try again.`,
        );
        return;
      }
      // Persist the marketing choice so the browser ad tags honour it (canTrack
      // reads this) AND the server-side lead conversion has a consent row to
      // gate on. Record consent BEFORE attribution so the enqueue that the
      // attribution write triggers sees the committed consent_log row.
      setMarketingConsent(consentMarketing);
      const contactEmail = email.trim();
      if (consentMarketing && contactEmail) {
        try {
          const slug = documentSlugForGuestConsent("marketing_optin");
          await supabase.rpc("log_consent_anon", {
            p_email: contactEmail,
            p_tenant_id: tenant.id,
            p_document_slug: slug,
            p_document_version: LEGAL_DOCUMENT_VERSIONS[slug],
            p_checkbox_text: GUEST_BOOKING_CONSENTS.marketing_optin.text,
            p_consent_kind: GUEST_BOOKING_CONSENTS.marketing_optin.kind,
            p_granted: true,
            p_metadata: { source: "widget_enquiry" },
          });
        } catch {
          // Best-effort — never block the enquiry on a consent-log write.
        }
      }
      // Attach ad-click/campaign attribution to the lead. This ALSO fires the
      // server-side lead conversion (trigger on enquiries.campaign_metadata).
      if (resp.enquiry_id) {
        void recordAttribution({ enquiryId: resp.enquiry_id });
      }
      // Browser-side lead conversion. eventId = enquiry id so the server CAPI
      // Lead (same dedup_key) dedupes against this browser event.
      trackLead({
        value: budget ? parseFloat(budget) : undefined,
        currency: tenant.currency ?? "ZAR",
        eventId: resp.enquiry_id,
      });
      // The venue's own tags (an embed on its site), through its dataLayer:
      // never personal data (state/host-datalayer.ts).
      pushHostLead({
        tenant: tenant.slug,
        enquiryId: resp.enquiry_id,
        value: budget ? parseFloat(budget) : null,
        currency: tenant.currency ?? "ZAR",
      });
      setSuccess(true);
    });
  }

  if (success) {
    return (
      <section className="vt-panel">
        <div className="vt-confirmed">
          <div className="vt-check">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h3>Thanks — we&apos;ll be in touch</h3>
          <p className="vt-muted">
            Our team will reply to {email || phone} within one business day.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="vt-panel vt-enquiry-panel">
      <div className="vt-service-card">
      <div className="vt-hero">
        <div className="vt-hero-gradient vt-hero-enquiry" />
        <div className="vt-hero-overlay">
          <div className="vt-hero-overlay-text">
            <h2>{heading?.trim() || "Plan with us"}</h2>
            <p className="vt-hero-tag">
              {tagline?.trim() ||
                "Weddings, conferences, private events — tell us what you're dreaming up and we'll reply with a tailored quote."}
            </p>
          </div>
        </div>
      </div>

      <div className="vt-service-body">
      <form className="vt-checkout" onSubmit={submit}>
        <div>
          <div className="vt-section-label">
            {enquiryServices.length > 0
              ? "What are you enquiring about?"
              : "What kind of event?"}
          </div>
          <div
            className="vt-dine-chip-row"
            style={
              chipCase && chipCase !== "none"
                ? { textTransform: chipCase as React.CSSProperties["textTransform"] }
                : undefined
            }
          >
            {KINDS.map((k) => (
              <button
                key={k.value}
                type="button"
                className={`vt-chip ${
                  !serviceId && kind === k.value ? "vt-chip-active" : ""
                }`}
                onClick={() => {
                  setServiceId(null);
                  setKind(k.value);
                }}
              >
                {kindLabels?.[k.value]?.trim() || k.label}
              </button>
            ))}
            {enquiryServices.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`vt-chip ${serviceId === s.id ? "vt-chip-active" : ""}`}
                onClick={() => {
                  setServiceId(s.id);
                  // Venue preference is a functions concept — clear any pick
                  // so it can't linger invisibly on a service enquiry.
                  setVenue("");
                }}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>

        <div className="vt-form-row">
          <div className="vt-field">
            <label>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="vt-field">
            <label>Company (optional)</label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>
        </div>

        <div className="vt-form-row">
          <div className="vt-field">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="vt-field">
            <label>Phone</label>
            <input
              type="tel"
              placeholder="+27 82 123 4567"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                if (phoneError) setPhoneError(null);
              }}
              onBlur={() => {
                // Normalise as soon as the guest leaves the field so they see
                // the international form we'll actually message.
                if (!phone.trim()) return setPhoneError(null);
                const check = normalizeOptionalPhone(phone);
                if (check.ok) {
                  if (check.e164) setPhone(check.e164);
                  setPhoneError(null);
                } else {
                  setPhoneError(check.error ?? null);
                }
              }}
              aria-invalid={phoneError != null}
            />
            {phoneError && (
              <div className="vt-field-error" role="alert">
                {phoneError}
              </div>
            )}
          </div>
        </div>

        <div className="vt-form-row-3">
          <div className="vt-field">
            <label>Preferred date</label>
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
            />
          </div>
          <div className="vt-field">
            <label>Party size</label>
            <input
              type="number"
              min="1"
              value={partySize}
              onChange={(e) => setPartySize(e.target.value)}
            />
          </div>
          <div className="vt-field">
            <label>Budget (ZAR, optional)</label>
            <input
              type="number"
              min="0"
              step="100"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
          </div>
        </div>

        <div className="vt-form-row">
          <label className="vt-checkbox-row">
            <input
              type="checkbox"
              checked={flexible}
              onChange={(e) => setFlexible(e.target.checked)}
            />
            <span>My date is flexible</span>
          </label>
          {/* Venue preference is a FUNCTIONS concept (wedding in the cellar
              vs the lawn). A service-subject enquiry (Stay, Stoep, Spa, …)
              already names its subject — the dropdown only confused guests. */}
          {!serviceId && (
            <div className="vt-field vt-field-chevron">
              <label>Venue preference</label>
              <select
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
              >
                <option value="">No preference</option>
                {venues.map((v) => (
                  <option key={v.id} value={v.name}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="vt-field">
          <label>Tell us more</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder="Theme, style, catering preferences, anything we should know."
          />
        </div>

        <label className="vt-consent-row">
          <input
            type="checkbox"
            checked={consentMarketing}
            onChange={(e) => setConsentMarketing(e.target.checked)}
          />
          <span>{GUEST_BOOKING_CONSENTS.marketing_optin.text}</span>
        </label>

        {error && <div className="vt-capacity-error">{error}</div>}

        {/* Ghost "Clear" on the left + primary "Send enquiry" on the
            right — matches the prototype's .actions row. */}
        <div className="vt-action-row vt-action-row-end">
          <button
            type="button"
            className="vt-btn-ghost"
            onClick={() => {
              setKind("wedding");
              setName("");
              setEmail("");
              setPhone("");
              setPhoneError(null);
              setCompany("");
              setEventDate("");
              setFlexible(true);
              setPartySize("");
              setBudget("");
              setVenue("");
              setMessage("");
              setConsentMarketing(false);
              setError(null);
            }}
            disabled={pending}
          >
            Clear
          </button>
          <button type="submit" className="vt-cta-inline" disabled={pending}>
            {pending ? "Sending…" : "Send enquiry →"}
          </button>
        </div>

        <p className="vt-muted vt-text-center" style={{ fontSize: 11 }}>
          No payment now — our team will reply with a quote.
        </p>
      </form>
      </div>
      </div>
    </section>
  );
}
